<ng-container *transloco="let t">
  <div
    id="add-bank-account-dialog"
    class="bg-white rounded-2xl shadow-xl max-w-[95vw] w-full max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col"
  >
    <div class="flex items-center justify-between p-6 border-b border-ld-border">
      <h2 id="add-bank-account-dialog-title" class="text-xl font-bold font-dm text-ld-neutral m-0">
        {{ t('modalsAddBankAccountTitle') }}
      </h2>
      <button
        id="add-bank-account-dialog-close-btn"
        type="button"
        class="p-2 rounded-full hover:bg-ld-neutral-100 transition-colors cursor-pointer"
        (click)="onClose()"
        [attr.aria-label]="t('commonAriaClose')"
      >
        <mat-icon class="text-ld-neutral-500">close</mat-icon>
      </button>
    </div>

    <div class="flex-1 overflow-y-auto p-6">
      @if (isHeloc()) {
        <p id="add-bank-account-subtitle-heloc" class="text-ld-neutral-500 text-sm m-0">
          {{ t('modalsAddBankAccountSubtitleHeloc') }}
          <a
            id="heloc-faq-link"
            [href]="faqLink()"
            target="_blank"
            rel="noopener noreferrer"
            class="text-ld-link hover:underline cursor-pointer"
          >
            {{ t('mfaFaqLink') }}
          </a>
          {{ t('modalsNewServicingLoanFaqMessageSuffix') }}
        </p>
      } @else {
        <p id="add-bank-account-subtitle" class="text-ld-neutral-500 text-sm m-0">
          {{ t('modalsAddBankAccountSubtitle') }}
        </p>
      }

      <form
        id="add-bank-account-form"
        [formGroup]="bankAccountForm"
        (ngSubmit)="onAddAccount()"
        class="mt-4 flex flex-col gap-5"
      >
        <mat-form-field id="account-nickname-field" appearance="outline" class="w-full">
          <mat-label>{{ t('modalsAddBankAccountAccountNickname') }}</mat-label>
          <input matInput id="account-nickname-input" type="text" formControlName="accountNickname" maxlength="100" />
          @if (bankAccountForm.get('accountNickname')?.hasError('maxlength')) {
            <mat-error id="account-nickname-maxlength-error">
              {{ t('modalsAddBankAccountNicknameMaxLength') }}
            </mat-error>
          } @else if (bankAccountForm.get('accountNickname')?.hasError('pattern')) {
            <mat-error id="account-nickname-pattern-error">{{ t('modalsAddBankAccountNicknamePattern') }}</mat-error>
          }
        </mat-form-field>

        @if (isHeloc()) {
          <div id="ffc-question-section" class="flex flex-col gap-2">
            <p id="ffc-question-text" class="font-dm text-sm font-normal text-ld-neutral-500 m-0">
              {{ t('modalsAddBankAccountFfcQuestion') }}
              <a
                id="ffc-link"
                [href]="faqLink() + '#what-is-a-ffc-for-further-credit-transfer-wire-transfer'"
                target="_blank"
                rel="noopener noreferrer"
                class="text-ld-link hover:underline cursor-pointer"
              >
                {{ t('modalsAddBankAccountFfcLink') }}
              </a>
              {{ t('modalsAddBankAccountFfcQuestionSuffix') }}
            </p>
            <div id="ffc-radio-group" class="bg-ld-neutral-50 rounded-lg p-4">
              <mat-radio-group
                id="ffc-radio"
                class="flex gap-6"
                [value]="ffcSelection()"
                (change)="onFfcChange($event.value)"
              >
                <mat-radio-button id="ffc-yes-radio" value="yes">
                  {{ t('commonYes') }}
                </mat-radio-button>
                <mat-radio-button id="ffc-no-radio" value="no">
                  {{ t('commonNo') }}
                </mat-radio-button>
              </mat-radio-group>
            </div>
          </div>
        }

        <fieldset
          id="ffc-disabled-fieldset"
          [disabled]="ffcSelection() === 'yes'"
          class="flex flex-col gap-5 border-none p-0 m-0 transition-opacity"
          [class.opacity-50]="ffcSelection() === 'yes'"
        >
          <div id="account-type-section" class="flex flex-col gap-3">
            <span class="font-dm text-sm font-normal text-ld-neutral-500">
              {{ t('commonFieldsAccountType') }}
            </span>
            @if (data.hideAccountOwnerTypeToggle) {
              <div class="bg-ld-neutral-50 rounded-lg p-4">
                <mat-radio-group formControlName="accountType" class="flex gap-6">
                  <mat-radio-button id="checking-radio" value="checking">
                    {{ t('modalsAddBankAccountChecking') }}
                  </mat-radio-button>
                  <mat-radio-button id="savings-radio" value="savings">
                    {{ t('modalsAddBankAccountSavings') }}
                  </mat-radio-button>
                </mat-radio-group>
              </div>
            } @else {
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="bg-ld-neutral-50 rounded-lg p-4">
                  <mat-radio-group
                    formControlName="accountOwnerType"
                    (change)="onAccountOwnerTypeChange()"
                    class="flex gap-6"
                  >
                    <mat-radio-button id="personal-radio" value="personal">
                      {{ t('modalsAddBankAccountPersonal') }}
                    </mat-radio-button>
                    <mat-radio-button id="business-radio" value="business">
                      {{ t('modalsAddBankAccountBusiness') }}
                    </mat-radio-button>
                  </mat-radio-group>
                </div>
                <div class="bg-ld-neutral-50 rounded-lg p-4">
                  <mat-radio-group formControlName="accountType" class="flex gap-6">
                    <mat-radio-button id="checking-radio" value="checking">
                      {{ t('modalsAddBankAccountChecking') }}
                    </mat-radio-button>
                    <mat-radio-button id="savings-radio" value="savings">
                      {{ t('modalsAddBankAccountSavings') }}
                    </mat-radio-button>
                  </mat-radio-group>
                </div>
              </div>
            }
          </div>

          @if (bankAccountForm.get('accountOwnerType')?.value === 'personal') {
            <div id="personal-name-fields" class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <mat-form-field id="first-name-field" appearance="outline" class="w-full">
                <mat-label>{{ t('modalsAddBankAccountFirstName') }}</mat-label>
                <input matInput id="first-name-input" type="text" formControlName="firstName" maxlength="100" />
                @if (bankAccountForm.get('firstName')?.hasError('required')) {
                  <mat-error id="first-name-required-error">{{ t('commonLabelsRequired') }}</mat-error>
                } @else if (bankAccountForm.get('firstName')?.hasError('maxlength')) {
                  <mat-error id="first-name-maxlength-error">
                    {{ t('modalsAddBankAccountFirstNameMaxLength') }}
                  </mat-error>
                } @else if (bankAccountForm.get('firstName')?.hasError('pattern')) {
                  <mat-error id="first-name-pattern-error">{{ t('modalsAddBankAccountFirstNamePattern') }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field id="last-name-field" appearance="outline" class="w-full">
                <mat-label>{{ t('modalsAddBankAccountLastName') }}</mat-label>
                <input matInput id="last-name-input" type="text" formControlName="lastName" maxlength="100" />
                @if (bankAccountForm.get('lastName')?.hasError('required')) {
                  <mat-error id="last-name-required-error">{{ t('commonLabelsRequired') }}</mat-error>
                } @else if (bankAccountForm.get('lastName')?.hasError('maxlength')) {
                  <mat-error id="last-name-maxlength-error">{{ t('modalsAddBankAccountLastNameMaxLength') }}</mat-error>
                } @else if (bankAccountForm.get('lastName')?.hasError('pattern')) {
                  <mat-error id="last-name-pattern-error">{{ t('modalsAddBankAccountLastNamePattern') }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field id="routing-number-field" appearance="outline" subscriptSizing="dynamic" class="w-full">
                <mat-label>{{ t('commonFieldsRoutingNumber') }}</mat-label>
                <input
                  matInput
                  id="routing-number-input"
                  type="text"
                  formControlName="routingNumber"
                  maxlength="9"
                  glance_masked="true"
                  inputmode="numeric"
                  ldNumericOnly
                  (blur)="onRoutingNumberBlur()"
                  (input)="onRoutingNumberInput()"
                />
                @if (isValidatingRouting()) {
                  <mat-hint id="routing-validating">{{ t('modalsAddBankAccountValidating') }}</mat-hint>
                } @else if (isRoutingValidated() && bankName()) {
                  <mat-hint id="routing-bank-name" class="text-green-600!">{{ bankName() }}</mat-hint>
                }
                @if (bankAccountForm.get('routingNumber')?.hasError('invalidRouting')) {
                  <mat-error id="routing-number-invalid-error">{{ t('errorsInvalidRoutingNumber') }}</mat-error>
                } @else {
                  <mat-error id="routing-number-error">{{ t('modalsAddBankAccountRoutingError') }}</mat-error>
                }
              </mat-form-field>
            </div>
          } @else {
            <div id="business-name-fields" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <mat-form-field id="business-name-field" appearance="outline" class="w-full">
                <mat-label>{{ t('modalsAddBankAccountBusinessNameLabel') }}</mat-label>
                <input matInput id="business-name-input" type="text" formControlName="businessName" maxlength="100" />
                @if (bankAccountForm.get('businessName')?.hasError('required')) {
                  <mat-error id="business-name-required-error">{{ t('commonLabelsRequired') }}</mat-error>
                } @else if (bankAccountForm.get('businessName')?.hasError('maxlength')) {
                  <mat-error id="business-name-maxlength-error">
                    {{ t('modalsAddBankAccountBusinessNameMaxLength') }}
                  </mat-error>
                } @else if (bankAccountForm.get('businessName')?.hasError('pattern')) {
                  <mat-error id="business-name-pattern-error">
                    {{ t('modalsAddBankAccountBusinessNamePattern') }}
                  </mat-error>
                }
              </mat-form-field>
              <mat-form-field
                id="routing-number-field-business"
                appearance="outline"
                subscriptSizing="dynamic"
                class="w-full"
              >
                <mat-label>{{ t('commonFieldsRoutingNumber') }}</mat-label>
                <input
                  matInput
                  id="routing-number-input-business"
                  type="text"
                  formControlName="routingNumber"
                  maxlength="9"
                  glance_masked="true"
                  inputmode="numeric"
                  ldNumericOnly
                  (blur)="onRoutingNumberBlur()"
                  (input)="onRoutingNumberInput()"
                />
                @if (isValidatingRouting()) {
                  <mat-hint id="routing-validating-business">{{ t('modalsAddBankAccountValidating') }}</mat-hint>
                } @else if (isRoutingValidated() && bankName()) {
                  <mat-hint id="routing-bank-name-business" class="text-green-600!">{{ bankName() }}</mat-hint>
                }
                @if (bankAccountForm.get('routingNumber')?.hasError('invalidRouting')) {
                  <mat-error id="routing-number-invalid-error-business">
                    {{ t('errorsInvalidRoutingNumber') }}
                  </mat-error>
                } @else {
                  <mat-error id="routing-number-error-business">{{ t('modalsAddBankAccountRoutingError') }}</mat-error>
                }
              </mat-form-field>
            </div>
          }

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <mat-form-field id="account-number-field" appearance="outline" class="w-full">
              <mat-label>{{ t('commonFieldsAccountNumber') }}</mat-label>
              <input
                matInput
                id="account-number-input"
                type="text"
                formControlName="accountNumber"
                maxlength="17"
                glance_masked="true"
                inputmode="numeric"
                ldNumericOnly
                (input)="onAccountNumberInput()"
              />
              <mat-error id="account-number-error">{{ t('modalsAddBankAccountAccountNumberError') }}</mat-error>
            </mat-form-field>
            <mat-form-field id="confirm-account-number-field" appearance="outline" class="w-full">
              <mat-label>{{ t('confirmAccountNumber') }}</mat-label>
              <input
                matInput
                id="confirm-account-number-input"
                type="text"
                formControlName="confirmAccountNumber"
                maxlength="17"
                glance_masked="true"
                inputmode="numeric"
                ldNumericOnly
                [blockPaste]="true"
                (input)="onConfirmAccountNumberInput()"
              />
              @if (bankAccountForm.get('confirmAccountNumber')?.hasError('mismatch')) {
                <mat-error id="confirm-account-number-error">
                  {{ t('modalsAddBankAccountAccountNumbersMustMatch') }}
                </mat-error>
              }
            </mat-form-field>
          </div>

          <div id="account-options" class="flex flex-wrap gap-x-6 gap-y-2">
            <mat-checkbox id="save-account-checkbox" formControlName="saveAccount" (change)="onSaveAccountChange()">
              {{ t('modalsAddBankAccountSaveBankAccount') }}
            </mat-checkbox>
            <mat-checkbox
              id="make-default-checkbox"
              formControlName="makeDefault"
              [disabled]="!bankAccountForm.get('saveAccount')?.value"
            >
              {{ t('modalsAddBankAccountMakeDefault') }}
            </mat-checkbox>
          </div>

          <div
            id="check-terms-section"
            class="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4 md:gap-6 items-start border-t border-ld-border pt-6"
          >
            <div id="check-image-container" class="flex justify-center mx-auto max-w-[180px] md:max-w-none">
              <img
                [src]="checkDiagramSrc"
                alt="Sample check showing where to find routing number and account number"
                class="w-full h-auto"
                id="check-diagram-image"
              />
            </div>
            <div id="terms-text" class="font-dm text-xs text-ld-neutral-500 leading-relaxed">
              {{ t('modalsAddBankAccountTermsText1') }}
              <a
                id="terms-link"
                role="link"
                tabindex="0"
                class="text-ld-link hover:underline cursor-pointer"
                (click)="openTermsModal($event)"
                (keydown.enter)="openTermsModal($event)"
              >
                {{ t('commonAddBankAccountTermsLink') }}
              </a>
              {{ t('modalsAddBankAccountTermsText2') }}
              <a
                id="ecomm-link"
                role="link"
                tabindex="0"
                class="text-ld-link hover:underline cursor-pointer"
                (click)="openElectronicCommsModal($event)"
                (keydown.enter)="openElectronicCommsModal($event)"
              >
                {{ t('modalsAddBankAccountEcommLink') }}
              </a>
              .
            </div>
          </div>
        </fieldset>
      </form>
    </div>

    <div class="flex flex-col gap-3 p-6 border-t border-ld-border bg-white">
      @if (errorMessage()) {
        <div id="add-bank-account-error" class="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-red-600 text-sm m-0">{{ errorMessage() }}</p>
        </div>
      }
      <div class="flex justify-end gap-3">
        <button
          id="add-bank-account-cancel-btn"
          type="button"
          class="btn-outline-ld-purple cursor-pointer"
          (click)="onCancel()"
        >
          {{ t('commonButtonsCancel') }}
        </button>
        <button
          id="add-bank-account-submit-btn"
          type="button"
          class="btn-primary-ld-purple cursor-pointer"
          [disabled]="!isFormValid || isLoading()"
          (click)="onAddAccount()"
        >
          @if (isLoading()) {
            <span>{{ t('modalsAddBankAccountAdding') }}</span>
          } @else {
            <span>{{ t('commonMessagesAddAccount') }}</span>
          }
        </button>
      </div>
    </div>
  </div>

  @if (showAccountValidationErrorModal()) {
    <div
      id="account-validation-error-backdrop"
      class="fixed inset-0 bg-black/50 z-60 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="account-validation-error-title"
    >
      <div
        id="account-validation-error-modal"
        class="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 flex flex-col items-center text-center"
        tabindex="-1"
        (click)="$event.stopPropagation()"
        (keydown)="$event.stopPropagation()"
      >
        <img [src]="errorIcon" alt="Error" class="w-12 h-12 mb-4" />
        <h2 id="account-validation-error-title" class="text-lg font-bold text-ld-neutral m-0 mb-2">
          {{ t('errorsAccountValidationExhaustedTitle') }}
        </h2>
        <p id="account-validation-error-message" class="text-sm text-ld-neutral-500 m-0 mb-6">
          {{ t('errorsAccountValidationExhaustedMessage') }}
        </p>
        <button
          id="account-validation-error-close-btn"
          type="button"
          class="px-8 py-3 bg-ld-primary text-white font-dm text-sm font-semibold rounded-full hover:bg-ld-primary transition-colors cursor-pointer"
          (click)="closeAccountValidationErrorModal()"
        >
          {{ t('commonAriaClose') }}
        </button>
      </div>
    </div>
  }
  @if (showTermsModal()) {
    <ld-terms-and-conditions-modal (closeModal)="closeTermsModal()" />
  }
  @if (showElectronicCommsModal()) {
    <ld-payment-electronic-communications-disclosure (closeModal)="closeElectronicCommsModal()" />
  }
  @if (showRoutingErrorModal()) {
    <div
      id="routing-error-backdrop"
      class="fixed inset-0 bg-black/50 z-60 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="routing-error-title"
    >
      <div
        id="routing-error-modal"
        class="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 flex flex-col items-center text-center"
        tabindex="-1"
        (click)="$event.stopPropagation()"
        (keydown)="$event.stopPropagation()"
      >
        <img [src]="errorIcon" alt="Error" class="w-12 h-12 mb-4" />
        <h2 id="routing-error-title" class="text-lg font-bold text-ld-neutral m-0 mb-2">
          {{ t('errorsRoutingErrorTitle') }}
        </h2>
        <p id="routing-error-message" class="text-sm text-ld-neutral-500 m-0 mb-6">
          {{ t('errorsRoutingErrorMessage') }}
        </p>
        <button
          id="routing-error-close-btn"
          type="button"
          class="px-8 py-3 bg-ld-primary text-white font-dm text-sm font-semibold rounded-full hover:bg-ld-primary transition-colors cursor-pointer"
          (click)="onClose()"
        >
          {{ t('commonAriaClose') }}
        </button>
      </div>
    </div>
  }
  @if (showFfcErrorModal()) {
    <div
      id="ffc-error-backdrop"
      class="fixed inset-0 bg-black/50 z-60 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ffc-error-title"
    >
      <div
        id="ffc-error-modal"
        class="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 flex flex-col text-left"
        tabindex="-1"
        (click)="$event.stopPropagation()"
        (keydown)="$event.stopPropagation()"
      >
        <div class="flex items-center gap-3 mb-4">
          <img [src]="errorIcon" alt="Error" class="w-8 h-8" />
          <h2 id="ffc-error-title" class="text-lg font-bold text-ld-neutral m-0">
            {{ t('modalsAddBankAccountFfcErrorTitle') }}
          </h2>
        </div>
        <p id="ffc-error-message" class="text-sm text-ld-neutral-500 m-0 mb-6 leading-relaxed">
          {{ t('modalsAddBankAccountFfcErrorMessagePrefix') }}
          <a [href]="helocTel" class="text-ld-link font-medium hover:underline cursor-pointer whitespace-nowrap">
            {{ helocPhone }}
          </a>
          {{ t('modalsAddBankAccountFfcErrorMessageSuffix') }}
        </p>
        <div class="flex justify-end">
          <button
            id="ffc-error-close-btn"
            type="button"
            class="btn-primary-ld-purple cursor-pointer"
            (click)="closeFfcErrorModal()"
          >
            {{ t('commonAriaClose') }}
          </button>
        </div>
      </div>
    </div>
  }
</ng-container>
